import axios, { AxiosInstance, AxiosRequestConfig, AxiosHeaders } from 'axios';
import H from '../constants';
/**
 * axios based fetcher for use alongside swr
 *
 * @returns Promise resolving the data returned from the request
 * @throws Error result from axios on failed request
 */
export const fetcher =
  (opts: AxiosRequestConfig = {}) =>
  (url: string) =>
    axios.get(url, opts).then(resp => resp.data);

/**
 * axios based fetcher for use alongside swr
 *
 * includes headers required for requests to the flags server
 *
 * @returns Promise resolving the data returned from the request
 * @throws Error result from axios on failed request
 */
export const flagsFetcher = (url: string) =>
  axios
    .get(url, {
      headers: {
        Authorization: `Bearer ${H.FLAGS_KEY}`,
      },
    })
    .then(resp => resp.data);

export enum UserType {
  SUPPLIER = 'supplier',
  RETAILER = 'retailer',
}

export type ChatFetcherParams = [string, string, UserType];

export const serializeChatFetcherParams = (params: ChatFetcherParams) =>
  JSON.stringify(params);

/**
 * axios based fetcher for use alongside swr
 *
 * Includes headers required for requests to the chat server
 *
 * Ideally in the future we can include these context headers automatically
 * with axios interceptors, and cache revalidation for SWR, but for now we
 * have to make sure we have a serialised representation of all the context
 * dependent data to ensure swr doesn't serve up cached data.
 *
 * @example - when calling as supplier
 *
 * const token = useToken();
 * const { data, error } = useSWR(
 *   token && serializeChatFetcherParams([
 *     '/some/chat/url', supplierId, 'supplier'
 *   ]),
 *   chatFetcher(token || '')
 * )
 *
 * @example - when calling as retailer
 *
 * const token = useToken();
 * const { data, error } =useSWR(
 *   serializeChatFetcherParams([
 *     '/some/chat/url', retailerId, 'retailer'
 *   ]),
 *   chatFetcher(token || '')
 * )
 *
 * @returns Promise resolving the data returned from the request
 * @throws Error result from axios on failed request
 */
export const chatFetcher = (token: string) => async (stringParse: string) => {
  const [url, entityId, entityType] = JSON.parse(
    stringParse
  ) as ChatFetcherParams;
  return axios
    .get(url, {
      baseURL: H.CHAT_URL,
      headers: {
        'context-id': entityId,
        'context-entity': entityType,
        Authorization: `Bearer ${token}`,
      },
    })
    .then(resp => resp.data);
};

/**
 * axios based fetcher for use alongside swr
 *
 * Fetcher to retrieve data from venue insights API
 *
 * @example - when calling
 *
 * const { data, error } = useSWR(
 *  ['v1/retailer/:retailerId/orders', 'Bearer token...']
 * ],
 *   authFetcher()
 * )
 *
 */
export const authFetcher = content => {
  const headers: {
    Authorization?: '';
  } = {};
  const [url, accessToken] = content;
  if (accessToken) headers.Authorization = accessToken;
  return axios
    .get(url, {
      headers,
    })
    .then(res => res.data);
};

/**
 * axios based fetcher for use alongside swr
 *
 * Fetcher to retrieve data from venue insights API
 *
 * @example - when calling
 *
 * const urlArray: string[] = ['...url1', '....url2']
 * const { data, error } = useSWR(
 *  { urlArray, args }
 * ],
 * arrayFetcher
 * )
 *
 */
export const arrayFetcher = ({ urlArray, args }) =>
  Promise.all(urlArray.map(url => authFetcher([url, args.token || ''])));

/**
 * axios based fetcher for use alongside swr
 *
 * Generic fetcher when wanting to post to the server
 *
 * @example - when calling as supplier
 *
 * const { data, error } = useSWR([
 *  'products/:productId/',
 *   JSON.stringify({
 *      supplierId: '320921434-3115-40sb-mck230353'
 *      productId: productId
 *   })
 * ]),
 *   postFetcher()
 * )
 *
 * Note: to always stringify your payload as SWR will always be triggered on /every/ render if the payload is an object.
 */
export const postFetcher =
  () =>
  ([url, payload, token]: string[]) => {
    const headers = new AxiosHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return axios
      .post(url, JSON.parse(payload), { headers })
      .then(resp => resp.data);
  };

/**
 * axios based fetcher for use alongside swr
 *
 * Generic fetcher when wanting to post to the server
 *
 * @example - when calling as supplier
 *
 * const { data, error } = useSWR([
 *  'products/:productId/',
 *   JSON.stringify({
 *      supplierId: '320921434-3115-40sb-mck230353'
 *      productId: productId
 *   })
 * ]),
 *   postFetcher()
 * )
 *
 * Note: to always stringify your payload as SWR will always be triggered on /every/ render if the payload is an object.
 */
export const postFetcherV2 = () => content => {
  const [url, payload, token, reqHeader] = content;
  const headers = new AxiosHeaders({
    accept: 'application/json',
    'Content-Type': 'application/json',
  });

  if (reqHeader?.['context-entity'] && reqHeader?.['context-id']) {
    headers.set('context-entity', reqHeader['context-entity']);
    headers.set('context-id', reqHeader['context-id']);
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return axios
    .post(url, JSON.parse(payload), { headers })
    .then(resp => resp.data);
};

export const deleteFetcher =
  (opts: AxiosRequestConfig = {}) =>
  (url: string) =>
    axios.delete(url, opts).then(resp => resp?.data);

export const patchFetcher = () => content => {
  const [url, payload, token, reqHeader] = content;
  const headers = new AxiosHeaders({
    accept: 'application/json',
    'Content-Type': 'application/json',
  });

  if (reqHeader?.['context-entity'] && reqHeader?.['context-id']) {
    headers.set('context-entity', reqHeader['context-entity']);
    headers.set('context-id', reqHeader['context-id']);
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return axios
    .patch(url, JSON.parse(payload), { headers })
    .then(resp => resp?.data);
};

export type PostFormDataFetcherContent = [
  url: string,
  formData: FormData,
  token?: string | null,
  reqHeader?: { 'context-entity': string; 'context-id': string },
];

export const postFormDataFetcher =
  () => async (content: PostFormDataFetcherContent) => {
    const [url, formData, token, reqHeader] = content;
    const headers = new AxiosHeaders();

    if (reqHeader?.['context-entity'] && reqHeader?.['context-id']) {
      headers.set('context-entity', reqHeader['context-entity']);
      headers.set('context-id', reqHeader['context-id']);
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const resp = await axios.post(url, formData, { headers });
    return resp.data;
  };

export const putFetcher =
  () =>
  async (
    content: [
      url: string,
      headers: AxiosRequestConfig['headers'],
      payload: string,
    ]
  ) => {
    const [url, headers, payload] = content;
    return axios
      .put(url, JSON.parse(payload), { headers })
      .then(resp => resp?.data);
  };

/**
 * Axios based fetcher for assets. It removes the authorization header as that
 * will trigger CORS and fail. Also prevents leaking tokens to CloudFront.
 */
let assetAxiosInstance;

function getAssetAxiosInstance(): AxiosInstance {
  if (assetAxiosInstance) {
    return assetAxiosInstance;
  }

  assetAxiosInstance = axios.create();
  delete assetAxiosInstance.defaults.headers.common.Authorization;

  return assetAxiosInstance;
}

export const assetsFetcher =
  (opts: AxiosRequestConfig = {}) =>
  (url: string) =>
    getAssetAxiosInstance()
      .get(url, {
        ...opts,
        headers: { ...opts.headers, 'Content-type': 'text/plain' },
      })
      .then(resp => resp.data);
